import { Theme } from "@/types/themes";

export type UserType =
  | "Student"
  | "Professional"
  | "Researcher"
  | "Fun learner";
export type EducationLevel = "Primary" | "JHS" | "SHS" | "University";
export type GradeLevel =
  | "pre_k"
  | "kindergarten"
  | "lower_primary"
  | "primary_1"
  | "primary_2"
  | "primary_3"
  | "primary_4"
  | "primary_5"
  | "primary_6"
  | "middle_school"
  | "high_school"
  | "college";
export const KIDS_GRADE_LEVELS: GradeLevel[] = [
  "pre_k",
  "kindergarten",
  "lower_primary",
  "primary_1",
  "primary_2",
  "primary_3",
  "primary_4",
  "primary_5",
];
export type LayoutType =
  | "ColorfulTutoring"
  | "StructuredAssessment"
  | "AcademicWorkspace"
  | "CleanWorkspace"
  | "DataDense";
export type FeatureEmphasis = "high" | "medium" | "low";
export type ExplanationComplexity = "simple" | "moderate" | "detailed";
export type LearningIntent =
  | "flashcards"
  | "AI chat"
  | "quizzes"
  | "summaries"
  | "all-round";
/**
 * "paper" shows every question at once (paginated), like a printed exam
 * paper — answers reveal on demand via a per-question button. "sequential"
 * shows one question at a time with Next/Prev navigation. Only applies to
 * practice quizzes; exam simulation always uses its own full-paper,
 * answers-hidden-until-submit view regardless of this preference.
 */
export type QuizLayout = "paper" | "sequential";

export interface UserPreferences {
  // Onboarding completion
  onboarding_completed: boolean;
  completed_at?: string;
  grade_level?: GradeLevel;
  /** @deprecated Use `grade_level`. Kept temporarily for compatibility while callers migrate. */
  gradeLevel?: GradeLevel;

  // User profile
  user_type: UserType;
  education_level?: EducationLevel;
  work_area?: string;
  country?: string;
  /** ISO 3166-1 alpha-2. Canonical source for billing currency. */
  country_code?: string;
  target_exam?: string;
  programme?: string;
  /** School year from onboarding (e.g. "shs_2", "jhs_1", "primary_4", "shs_private"); stored in users.preferences. */
  grade?: string;
  target_date?: string;
  currency?: string;
  preferred_language?: string;
  bio?: string;

  /**
   * The student's own words for why they're studying — a personal reward,
   * a specific exam target, a story that keeps them going. Referenced
   * directly in notification copy, so it should stay free-text rather than
   * a fixed enum ("Pass WASSCE with 8 A's and meet the President at my
   * school's 70th anniversary" is a real example, not a placeholder).
   */
  study_goal?: string;

  // Learning preferences
  learning_intents: LearningIntent[];
  gamification_enabled: boolean;
  explanation_complexity: ExplanationComplexity;

  // University / tertiary context
  major?: string;        // e.g. "Computer Science", "Civil Engineering"
  degree_type?: string;  // "undergraduate" | "postgraduate" | "diploma"

  // Dashboard configuration
  layout: LayoutType;
  feature_emphasis: {
    quiz: FeatureEmphasis;
    flashcards: FeatureEmphasis;
    summarize: FeatureEmphasis;
    chat: FeatureEmphasis;
  };

  // AI configuration
  system_prompt: string;
  ai_enabled?: boolean;

  // Personalization
  welcome_message: string;

  // Study subjects
  subjects?: string[];
  ghanaian_languages?: string[];

  // Theme
  ui_theme?: string;
  theme_mode?: "light" | "dark";

  // Notification & Habit settings
  notifications?: {
    study_reminders: boolean;
    achievement_alerts: boolean;
    product_updates: boolean;

    // New habit-specific fields
    intensity: "off" | "light" | "balanced" | "intense";
    streak_reminders: boolean;
    goal_countdowns: boolean;
    smart_nudges: boolean;
    preferred_times: string[]; // 'morning', 'afternoon', 'evening'
    quiet_hours_enabled: boolean;
    quiet_hours_start: string;
    quiet_hours_end: string;
  };

  // Avatar
  avatar_seed?: string;

  // Analytics
  analytics_enabled?: boolean;

  // App UI state
  sidebar_collapsed?: boolean;
  tutorial_completed?: boolean;
  minimalUI?: boolean;
  hideTimer?: boolean;
  hideStreak?: boolean;

  // Quiz defaults
  objectiveQuestionsCount?: number;
  subjectiveQuestionsCount?: number;
  quiz_layout?: QuizLayout;
  /** Saved practice question-type preset (users.preferences JSONB). Null = follow the exam's format. */
  quiz_question_types?: string[] | null;
  walkthroughs_seen?: string[];
  /** Explicit reset command; removed by the database receipt-merge trigger. */
  walkthroughs_reset?: boolean;

  // Smart Scheduler
  scheduling_onboarded?: boolean;
  scheduling_type?: "daily" | "weekly";
  daily_study_hours?: number;
  weekly_schedule?: Record<string, { start: string; end: string }[]>;
  study_strategy?: "strict" | "balanced";

  // Text-to-Speech (TTS) settings
  tts?: {
    enabled: boolean;
    voice: "MALE" | "FEMALE";
    speed: number; // 0.5 to 2.0
    auto_play_responses: boolean; // Auto-play AI responses
    auto_play_summaries: boolean; // Auto-play document summaries
  };
}

export interface UserPreferencesContextType {
  /** Account whose preferences have successfully loaded from the server. */
  loadedForUserId?: string;
  preferences: UserPreferences | null;
  isLoading: boolean;
  isThemeLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  updatePreferences: (updates: Partial<UserPreferences>) => Promise<void>;
  /** Patches local state (and its localStorage cache) only — no RPC round trip.
   *  For a value the caller already knows was just written server-side (e.g.
   *  onboarding completion), so route guards reading `preferences` don't have
   *  to wait on a refetch that may not have flushed before a navigation. */
  patchLocalPreferences: (updates: Partial<UserPreferences>) => void;
  currentTheme: Theme | null;
  updateUiTheme: (theme: string) => Promise<void>;
  updateThemeMode: (mode: "light" | "dark") => Promise<void>;
  toggleSidebarCollapsed: () => Promise<void>;
}

// Default preferences for fallback
export const DEFAULT_PREFERENCES: UserPreferences = {
  onboarding_completed: false,
  user_type: "Student",
  learning_intents: ["all-round"],
  gamification_enabled: true,
  explanation_complexity: "moderate",
  layout: "CleanWorkspace",
  feature_emphasis: {
    quiz: "medium",
    flashcards: "medium",
    summarize: "medium",
    chat: "medium",
  },
  system_prompt: "You are a helpful AI assistant for learning and studying.",
  ai_enabled: true,
  welcome_message: "Welcome back! Ready to continue your learning journey?",
  subjects: [],
  // Deliberately NOT defaulted to "Ghana". A fabricated country is what
  // caused every user who never set one to be billed in cedis. Undefined
  // resolves to USD, which is chargeable everywhere.
  country: undefined,
  country_code: undefined,
  target_exam: "WASSCE",
  currency: "GHS",
  preferred_language: "English (International)",
  ui_theme: "INK_EMBER", // Novari signature theme (Paper / Lamplight)
  theme_mode: "light",   // Paper is the default; Lamplight is an equal
  notifications: {
    study_reminders: true,
    achievement_alerts: true,
    product_updates: false,
    intensity: "balanced",
    streak_reminders: true,
    goal_countdowns: true,
    smart_nudges: true,
    preferred_times: ["morning", "evening"],
    quiet_hours_enabled: false,
    quiet_hours_start: "22:00",
    quiet_hours_end: "08:00",
  },
  analytics_enabled: true,
  tutorial_completed: false,
  quiz_layout: "sequential",
  tts: {
    enabled: true,
    voice: "MALE",
    speed: 1.0,
    auto_play_responses: false,
    auto_play_summaries: false,
  },
};
